import type { CreateSaleInput } from '@/api/types';
import { log } from './log';
import { OUTBOX, store } from './store';

/**
 * The queue of work taken at the counter but not yet accepted by the server.
 *
 * Append-only until an entry succeeds. Nothing is ever silently dropped: an
 * entry that the server refuses stays here in a failed state, visible and
 * countable, because the alternative is a bill in a customer's hand that exists
 * nowhere else.
 */

export type OutboxKind = 'sale';

export type OutboxEntry = {
  /** Auto-increment. Replay follows it, so bills reach the server in the order taken. */
  seq?: number;
  kind: OutboxKind;
  /**
   * Generated once, here, and sent as `Idempotency-Key` on every attempt.
   *
   * This is what makes replay safe. The server stores the response against the
   * key, so a retry after a reply that never arrived returns the original sale
   * rather than creating a second one — the difference between a customer being
   * billed once and twice.
   */
  idempotencyKey: string;
  pharmacyId: string;
  /** What was printed and handed over. Frozen at the moment of the sale. */
  payload: CreateSaleInput;
  /** The number this till assigned locally; see `billing.ts`. */
  billNumber: string;
  takenAt: string;
  attempts: number;
  lastError?: string;
  /** Set when the server refused it for a reason retrying will not fix. */
  failed?: boolean;
};

const newKey = (): string =>
  // crypto.randomUUID is available in every browser this app supports; the
  // fallback exists because an insecure origin (plain http on a LAN address)
  // does not expose it, and a counter on a shop LAN is a real deployment.
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const outbox = {
  /** Records work before anything else happens. The bill is not "taken" until this resolves. */
  async add(entry: Omit<OutboxEntry, 'seq' | 'attempts' | 'idempotencyKey'>): Promise<number> {
    const key = newKey();
    // Logged before AND after the write, so a queue that never commits is
    // visible as a line with no answer rather than as a button that spins.
    log.outbox(`writing ${entry.billNumber} to storage…`);
    const seq = (await store.put<OutboxEntry>(OUTBOX, {
      ...entry,
      idempotencyKey: key,
      attempts: 0,
    })) as number;
    /*
     * The line that proves a bill reached durable storage. Counts and ids only
     * — the payload holds a customer's name, number and medicines, and none of
     * that belongs in a console.
     */
    log.outbox(`queued #${seq} ${entry.billNumber}`, {
      kind: entry.kind,
      lines: entry.payload.lines.length,
      idempotencyKey: key,
    });
    return seq;
  },

  /** Oldest first — replay order is the order the counter took them in. */
  async pending(): Promise<OutboxEntry[]> {
    const all = await store.all<OutboxEntry>(OUTBOX);
    return all.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  },

  async update(entry: OutboxEntry): Promise<void> {
    await store.put<OutboxEntry>(OUTBOX, entry);
  },

  async remove(seq: number): Promise<void> {
    await store.remove(OUTBOX, seq);
    log.outbox(`sent and cleared #${seq}`);
  },

  async count(): Promise<number> {
    return store.count(OUTBOX);
  },
};
